import React, {useCallback, useMemo} from 'react';
import cx from 'classnames';
import {observer} from 'mobx-react';
import {Button, Flex, Icon, PriorityIcon} from '@priority-software/priority-style-react';
import {FormViewToggler} from '../FormViewToggler';
import {FormPagination} from '../FormPagination';
import {APIActionStatus, formsStore} from '../../../../stores/Form';
import {createTemporarilyRecord, getUpdateActionType} from '../../../../stores/Form/utils';
import {AdvancedSearches} from '../AdvancedSearch/AdvancedSearches';
import {ActionIcon} from '../../../../components/ActionIcon';
import {priority} from '@priority-software/priority-proto-api/lib';
import {Tag} from '../Tag';
import {ActionsDropdown} from '../ActionsDropdown';
import {actionZoom} from '../../actions/zoom';
import {EntityType} from '../../consts/EntityTypes';
import {callLinkedActions} from '../../actions/screen';
import {executeDirectActivation} from '../../actions/activations';
import {getEntityIcon} from '../../../../components/ActionIcon/actionIconData';
import {showConfirmMessage} from '../../../../_shared/Messages/actions/MessageActions';
import {defaultConfirmProps} from '../../actions/messages';
import {isNewApiForm, runForm} from '../../../../_shared/GWTBridge/invoke/netForm';
import {fieldValue} from '../../../../stores/Form/Field';
import {Form} from '../../../../stores/Form/Form';
import {Column} from '../../../../stores/Form/Column';
import {AutomationsModule} from '../../../Automations/modules/FormRules/FormAutomation.module';
import {createDataTestId} from '../../../../utils/dataTestId';
import {clearFormData} from '../../actions/filters';
import {texts} from '../AdvancedSearch/AdvancedSearchDrawer/texts';
import styles from './style.module.scss';

type Props = {
    currentForm: Form;
    openRecordDialog: () => void;
    isFormActive: boolean;
    dropdonwBodyClass?: string;
};

export const FormToolbar = observer((props: Props) => {
    const {currentForm, isFormActive, openRecordDialog} = props;
    const isParentCollapsed = currentForm.parentForm?.collapsed;

    const listActions: priority.netitems.form.IAction[] = useMemo(
        () => currentForm.actions?.slice(2),
        [currentForm.actions]
    );

    const toggleSearchMode = useCallback(() => {
        currentForm.setSearchModeLevel(currentForm.searchModeLevel ? 0 : 1);
    }, [currentForm]);

    const toggleParentFormView = async () => {
        await callLinkedActions(
            async () => formsStore.currentScreen?.handleFormLevel(currentForm.level),
            async () => {
                currentForm?.parentForm?.toggleViewState(!isParentCollapsed);
                return APIActionStatus.passed;
            }
        );
    };

    const createNewRecord = async () => {
        const currentScreen = formsStore.currentScreen;
        const isActionAllowed = await currentScreen.handleFormLevel(currentForm.level);
        const activeForm = currentScreen.getActiveForm();
        const currentRecord = activeForm.records[activeForm.currentRow];

        if (isActionAllowed) {
            if (currentForm.level === 0) {
                openRecordDialog();
            } else if (!currentRecord.isTemporary || !currentRecord.keys) {
                createTemporarilyRecord(activeForm);
            }
        }
    };

    const runAction = (action: priority.netitems.form.IAction) => {
        if (isActionDisabled()) {
            return;
        }
        if (action.Confirm) {
            showConfirmMessage({
                ...defaultConfirmProps(),
                title: action.Title,
                onConfirm: () => continueAction(action)
            });
        } else {
            continueAction(action);
        }
    };

    const continueAction = async (action: priority.netitems.form.IAction) => {
        const form = formsStore.getActiveForm();
        const screen = formsStore.getScreenBySession(form.session);
        const valueActionType = getUpdateActionType(form, true, screen.editMode);
        const isValidAction = await screen.checkAndSave(valueActionType);
        if (isValidAction) {
            if (action.Type === EntityType.FORM) {
                const ename: string = action.Ename;
                if (isNewApiForm(ename)) {
                    actionZoom(form, ename);
                } else {
                    const column: Column = form.currentColumn;
                    const value: fieldValue = form.currentRecord.getField(column.id).currentValue;
                    runForm(ename, Number(column.Column), column.Name, value.toString(), true);
                }
            } else {
                executeDirectActivation(form, action);
            }
        }
    };

    const isActionDisabled = () => {
        return !isFormActive || formsStore.currentScreen?.getActiveForm()?.currentRecord?.isEmpty();
    };
    const isShowClearAllDataButton =
        (currentForm.filters?.length || !currentForm.isEmpty) && !currentForm.parentForm;

    return (
        <Flex
            componentClasses={styles.toolbar}
            {...createDataTestId('form-toolbar-container', true)}
        >
            <Flex justifyContent="flex-start" componentClasses={styles.leftToolbarPart}>
                <Button
                    componentClasses={cx(styles.btn)}
                    prefixIcon={<Icon iconColor="white" icon={PriorityIcon.addSmall} />}
                    size="small"
                    skin="primary"
                    disabled={!!currentForm.searchModeLevel}
                    onClick={createNewRecord}
                >
                    {texts.newFormLabel}
                </Button>

                <AdvancedSearches currentForm={currentForm} />
                {isShowClearAllDataButton && (
                    <Button
                        skin="text"
                        prefixIcon={<Icon iconColor="primary-700" icon={PriorityIcon.clear} />}
                        componentClasses={styles.clearAllBtn}
                        onClick={() => clearFormData(currentForm)}
                        disabled={!isFormActive}
                    >
                        {texts.clearAll}
                    </Button>
                )}

                <FormPagination
                    form={currentForm}
                    componentClasses={styles.pagination}
                    formView={currentForm.view}
                    isActiveForm={isFormActive}
                />
            </Flex>

            <Flex justifyContent="flex-end" componentClasses={styles.rightToolbarPart}>
                {currentForm.actions?.slice(0, 2).map(action => (
                    <Tag
                        key={action?.Ename}
                        text={action?.Title}
                        onClick={() => runAction(action)}
                        componentClasses={getEntityIcon(action?.Type as number)?.hoverClass}
                        disabled={isActionDisabled()}
                        {...createDataTestId(`action-tag-${action?.Ename}`, true)}
                    >
                        <ActionIcon
                            disabled={isActionDisabled()}
                            type={action?.Type as number}
                            componentClasses={styles.iconWrapper}
                        />
                    </Tag>
                ))}

                {listActions.length > 0 && (
                    <ActionsDropdown
                        actions={listActions}
                        disabled={isActionDisabled()}
                        runAction={runAction}
                    />
                )}

                <div className={cx(styles.divider, styles.marginXmd)} />

                <AutomationsModule
                    form={currentForm}
                    componentClasses={styles.marginXsm}
                    disabled={!isFormActive}
                />

                <div className={cx(styles.divider, styles.marginXmd)} />

                <FormViewToggler
                    componentClasses={styles.marginXmd}
                    activeView={currentForm.view}
                    onViewChange={currentForm.toggleFormView}
                />

                <Icon
                    disabled={!isFormActive}
                    componentClasses={cx(styles.marginXmd, styles.btn, styles.f11icon, {
                        [styles.active]: currentForm.searchModeLevel
                    })}
                    icon={PriorityIcon.F11}
                    onClick={toggleSearchMode}
                />

                <div className={cx(styles.divider, styles.marginXmd)} />

                <Flex componentClasses={styles.marginXmd} inline>
                    <Icon componentClasses={styles.marginXsm} icon={PriorityIcon.delete} />
                    <Icon componentClasses={styles.marginXsm} icon={PriorityIcon.iconShare} />
                    <Icon componentClasses={styles.marginXsm} icon={PriorityIcon.notification} />
                </Flex>

                <div className={cx(styles.divider, styles.marginXmd)} />

                <Icon
                    componentClasses={cx(styles.marginXsm, styles.moreIcon)}
                    icon={PriorityIcon.iconMore}
                />

                {currentForm.level === 1 && (
                    <Icon
                        componentClasses={styles.expandBtn}
                        icon={isParentCollapsed ? PriorityIcon.shrink : PriorityIcon.expand}
                        onClick={toggleParentFormView}
                    />
                )}
            </Flex>
        </Flex>
    );
});
